import "./ForecastResults.scss";
import React, { useMemo, useState } from "react";
import { RouteComponentProps } from "@reach/router";
import { FullPageSpinner } from "../../Components";
import { useSetError } from "../../redux/modals";
import { DateGroupingKey } from "../../TVADCrossChannel/homePageConstants";
import {
  BestModelRow,
  FoldDataRow,
  FullForecastSelection,
  ModelSummaryData
} from "../forecastingConstants";
import {
  combineHistoricalPredictedData,
  formatBestModelData,
  formatFoldData
} from "../forecastingUtils";
import ForecastResults from "./ForecastResults";
import ModelResults from "./ModelResults";

interface ForecastResultsPageProps {
  selectedForecast: FullForecastSelection | undefined;
  foldData: FoldDataRow[] | undefined;
  modelSummaryData: ModelSummaryData | undefined;
  bestModelData: BestModelRow[] | undefined;
  budgetSelectableOptions: number[];
  selectNewBudget: (budget: number) => void;
  cid: string;
}

const ForecastResultsPage = ({
  selectedForecast,
  foldData,
  modelSummaryData,
  bestModelData,
  budgetSelectableOptions,
  selectNewBudget,
  cid,
}: RouteComponentProps & ForecastResultsPageProps): JSX.Element => {
  const [chartDateGrouping, setChartDateGrouping] = useState<DateGroupingKey>("Day");
  const setError = useSetError();

  const trainTestData = useMemo(() => {
    return formatFoldData(foldData);
  }, [foldData]);

  const selectedTrainTestData = useMemo(() => {
    if (!selectedForecast || !trainTestData) {
      return [];
    }

    return trainTestData[`${selectedForecast.kpiName}, ${selectedForecast.outcomeVar}`];
  }, [trainTestData, selectedForecast]);

  const forecastedBudgetDataByKpi = useMemo(() => {
    if (!bestModelData) {
      return {};
    }

    return formatBestModelData(
      bestModelData,
      selectedForecast?.comparisonBudgets.map(budget => String(budget)) ?? []
    );
  }, [bestModelData, selectedForecast?.comparisonBudgets]);

  const selectedForecastedBudgetData = useMemo(() => {
    if (!selectedForecast) {
      return [];
    }

    return forecastedBudgetDataByKpi[`${selectedForecast.kpiName}, ${selectedForecast.outcomeVar}`];
  }, [forecastedBudgetDataByKpi, selectedForecast]);

  const [combinedHistoricalPredictedData, earliestPredictedDate] = useMemo(() => {
    return combineHistoricalPredictedData(trainTestData, forecastedBudgetDataByKpi);
  }, [forecastedBudgetDataByKpi, trainTestData]);

  const selectedCombinedHistoricalPredictedData = useMemo(() => {
    if (!selectedForecast) {
      return [];
    }

    return combinedHistoricalPredictedData[
      `${selectedForecast.kpiName}, ${selectedForecast.outcomeVar}`
    ];
  }, [combinedHistoricalPredictedData, selectedForecast]);

  if (!selectedForecast) {
    return <FullPageSpinner />;
  }

  return (
    <div className="forecastResultsContainer">
      <ModelResults
        selectedForecast={selectedForecast}
        selectedTrainTestData={selectedTrainTestData}
        modelSummaryData={modelSummaryData}
        chartDateGrouping={chartDateGrouping}
        setChartDateGrouping={setChartDateGrouping}
        setError={setError}
      />
      <ForecastResults
        selectedForecast={selectedForecast}
        budgetSelectableOptions={budgetSelectableOptions}
        selectNewBudget={selectNewBudget}
        chartDateGrouping={chartDateGrouping}
        setChartDateGrouping={setChartDateGrouping}
        selectedTrainTestData={selectedTrainTestData}
        selectedForecastedBudgetData={selectedForecastedBudgetData}
        selectedCombinedHistoricalPredictedData={selectedCombinedHistoricalPredictedData}
        earliestPredictedDate={earliestPredictedDate}
        cid={cid}
        setError={setError}
      />
    </div>
  );
};

export default ForecastResultsPage;
